import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { STATS, WHY_CHOOSE_US } from "@/data/content";
import { ShieldCheck, Cpu, BarChart3, Wallet } from "lucide-react";

const ICONS = [ShieldCheck, Cpu, BarChart3, Wallet];

const CountUp = ({ value, suffix, decimals = 0, inView }) => {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setDisplay(value);
      return;
    }
    let start;
    const duration = 1400;
    let raf;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(value * eased);
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  return (
    <span>
      {display.toFixed(decimals)}
      {suffix}
    </span>
  );
};

export const WhyChooseUs = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="why-us" className="py-[var(--section-py)]" data-testid="stats-section">
      <div className="mx-auto max-w-[var(--container-max)] px-4 sm:px-6">
        <div className="mb-12 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[color:var(--gs-gold-400)]">
            Why Choose Us
          </p>
          <h2 className="font-display mt-3 text-3xl font-bold text-white sm:text-4xl" data-testid="why-us-heading">
            Built for results, priced for scale
          </h2>
        </div>

        {/* Stats row */}
        <div
          ref={ref}
          className="grid grid-cols-2 gap-4 rounded-[var(--radius)] border border-[color:var(--gs-line)] bg-[color:var(--gs-glass)] p-6 backdrop-blur sm:p-8 lg:grid-cols-4"
        >
          {STATS.map((stat) => (
            <div key={stat.label} className="text-center" data-testid="stat-item">
              <p className="font-display text-3xl font-bold text-[color:var(--gs-gold-400)] sm:text-4xl">
                <CountUp
                  value={stat.value}
                  suffix={stat.suffix}
                  decimals={stat.decimals || 0}
                  inView={inView}
                />
              </p>
              <p className="mt-1.5 text-xs text-white/60 sm:text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Why choose us cards */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          {WHY_CHOOSE_US.map((reason, idx) => {
            const Icon = ICONS[idx % ICONS.length];
            return (
              <div
                key={reason.title}
                data-testid="why-us-card"
                className="rounded-[var(--radius)] border border-white/10 bg-[color:var(--gs-glass)] p-6 backdrop-blur hover:border-[color:var(--gs-line)]"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-[color:var(--gs-line)] bg-[rgba(240,180,41,0.1)]">
                  <Icon className="h-5 w-5 text-[color:var(--gs-gold-400)]" />
                </div>
                <h3 className="font-display mt-4 text-base font-semibold text-white">
                  {reason.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-white/60">
                  {reason.description}
                </p>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
